/**
 * Guards against the one crash class that `react-native-svg` turns into a hard native abort.
 *
 * On iOS the software render path (`-[RNSVGSvgView drawRect:]`) walks the element tree and calls
 * `-[UIView setFrame:]` with each element's computed bounding box. `CALayer` raises
 * `NSInvalidArgumentException` for a NaN or infinite position, and because that happens inside a
 * CoreAnimation commit — not inside a JS call — there is no JS frame to catch it: the Obj-C
 * exception reaches `CPPExceptionTerminate` and the process aborts with SIGABRT. A single bad
 * number anywhere in an SVG subtree kills the app, with a stack that names only RNSVG internals
 * and no hint of which prop produced it.
 *
 * Non-finite values arrive from ordinary arithmetic, not from corrupt data: a division by a
 * gesture scale that is momentarily 0, a measurement read before layout, `Math.max()` of an empty
 * array. So every number computed at render time and passed to an SVG element goes through
 * `finiteSvgNumber`, and every divisor goes through `svgDivisor`.
 *
 * Both are worklet-safe: they are called from `useAnimatedProps` bodies that Reanimated runs on
 * the UI thread, where a thrown error would be just as fatal and far less debuggable.
 */

/** Smallest divisor that cannot produce a non-finite quotient for our coordinate ranges. */
const MIN_SVG_DIVISOR = 1e-6;

/**
 * Returns `value` when it is a finite number, otherwise `fallback`.
 *
 * `fallback` must itself be finite; a non-finite fallback would defeat the guard, so it is
 * coerced to 0 rather than trusted.
 */
export function finiteSvgNumber(value: number, fallback = 0): number {
  "worklet";
  if (Number.isFinite(value)) {
    return value;
  }
  return Number.isFinite(fallback) ? fallback : 0;
}

/**
 * Clamps a divisor away from zero so `x / svgDivisor(d)` is always finite.
 *
 * A gesture-driven scale can legitimately pass through 0 (and denormals below ~1e-308 divide to
 * Infinity just as 0 does), so this clamps magnitude rather than testing for an exact zero, while
 * preserving sign for divisors that may legitimately be negative.
 */
export function svgDivisor(value: number, fallback = 1): number {
  "worklet";
  const safeFallback =
    Number.isFinite(fallback) && Math.abs(fallback) >= MIN_SVG_DIVISOR ? fallback : 1;
  if (!Number.isFinite(value)) {
    return safeFallback;
  }
  if (Math.abs(value) < MIN_SVG_DIVISOR) {
    // `-0 < 0` is false, but `x / -0` is -Infinity, so negative zero must be detected explicitly
    // or the clamp silently flips the sign of every coordinate derived from it. `1 / -0` is the
    // one test that separates the two zeroes using only operators every worklet runtime supports.
    const isNegative = value < 0 || 1 / value === Number.NEGATIVE_INFINITY;
    return isNegative ? -MIN_SVG_DIVISOR : MIN_SVG_DIVISOR;
  }
  return value;
}

/**
 * Builds a `viewBox` string, forcing every field finite and both extents positive.
 *
 * A zero or negative width/height is not a crash on its own but renders nothing and, once scaled,
 * is the usual precursor to a non-finite coordinate downstream.
 */
export function finiteSvgViewBox(
  minX: number,
  minY: number,
  width: number,
  height: number,
): { height: number; minX: number; minY: number; viewBox: string; width: number } {
  "worklet";
  const safeMinX = finiteSvgNumber(minX, 0);
  const safeMinY = finiteSvgNumber(minY, 0);
  const safeWidth = Math.max(MIN_SVG_DIVISOR, finiteSvgNumber(width, 1));
  const safeHeight = Math.max(MIN_SVG_DIVISOR, finiteSvgNumber(height, 1));
  return {
    height: safeHeight,
    minX: safeMinX,
    minY: safeMinY,
    viewBox: `${safeMinX} ${safeMinY} ${safeWidth} ${safeHeight}`,
    width: safeWidth,
  };
}
